/**
 * @file: util.js
 * @author: liuye11(liuye11@baidu.com)
 */

import format from 'date-fns/format';
import getDay from 'date-fns/get_day';
import setDay from 'date-fns/set_day';
import isBefore from 'date-fns/is_before';
import isSameDay from 'date-fns/is_same_day';
import uri from '@baidu/ve-ria/utils/uri';
import alertManager from 'veui/managers/alert';
import {parseDate} from '@baidu/ve-ria/utils/date';
import {keys, extend, isObject, find} from 'lodash';

/**
 * 补全确实的天数。
 * 有可能前端传给后端的日期范围是20140101-20141212，然后后端只返回了20140102这一天的数据，
 * 此时需要前端自动补全其余天的数据为0。
 *
 * @param {Array} data 从后端拿到的数据
 * @param {Date} startDate 开始日期
 * @param {Date} endDate 结束日期
 * @param {Object} fillData 填充的默认
 * @return {Array}
 */
export function fillMissingDay(data, startDate, endDate, fillData) {
    startDate = parseDate(startDate);
    endDate = parseDate(endDate);

    for (
        let cursor = startDate;
        isSameDay(cursor, endDate) || isBefore(cursor, endDate);
        cursor = setDay(cursor, getDay(cursor) + 1)
    ) {
        let cursorFormatted = format(cursor, 'YYYYMMDD');
        let exists = find(data, item => item.date === cursorFormatted);
        if (!exists) {
            data.push(extend({date: cursorFormatted}, fillData));
        }
    }

    return data;
}

export function getProxyParam(idKey) {
    let params = window.location.search;
    if (params && params[0] === '?') {
        params = params.slice(1);
    }
    params = parseQuery(params);
    if (params[idKey]) {
        return {[idKey]: params[idKey]};
    }
}

export function addProxyParam(url, idKey) {
    let params = getProxyParam(idKey) || '';
    if (!params) {
        return url;
    }
    return '' + uri(url).addQuery(params);
}

/**
 * 跨entry的跳转，构造跳转url
 *
 * @param {string} entryName entry在constants中注册的名字
 * @param {string} [hashPath] hash路径
 * @param {Object} [params] hash参数
 * @return {string}
 */
export function getEntryUrl(constants, entryName, hashPath, params) {
    let entryPath = constants.getMap('ENTRY_URL')[entryName];
    return entryPath + location.search
        + (
            hashPath
                ? '#' + URL.withQuery(hashPath, params)
                : ''
        );
}

function parseQuery(str) {
    let pairs = str.split('&');
    let query = {};
    for (let i = 0; i < pairs.length; i++) {
        // 考虑到有可能因为未处理转义问题，
        // 导致value中存在**=**字符，因此不使用`split`函数
        let pair = pairs[i];
        if (!pair) {
            continue;
        }
        let index = pair.indexOf('=');
        // 没有**=**字符则认为值是**true**
        let key = index < 0
            ? decodeURIComponent(pair)
            : decodeURIComponent(pair.slice(0, index));
        let value = index < 0
            ? true
            : decodeURIComponent(pair.slice(index + 1));

        // 已经存在这个参数，且新的值不为空时，把原来的值变成数组
        if (query.hasOwnProperty(key)) {
            if (value !== true) {
                query[key] = [].concat(query[key], value);
            }
        }
        else {
            query[key] = value;
        }
    }
    return query;
}

export function isDir(file) {
    return !!(file.children && file.children.length);
}

export function isExtValid(file) {
    const ext =  (file.name || '').split('.').pop();
    const extensions = `xml,txt,js,jsx,ts,tsx,html,css,less,sass,scss,vue,styl,csv,
            svg,json,sh,es6,es,md,yml,babelrc,editorconfig,postcssrc,npmrc,npmignore`.split(',');
    return extensions.indexOf(ext) >= 0;
}

export function getItemClass(item, hasItem) {
    const classList = hasItem ? ['item'] : [];
    return classList.concat(isDir(item) ? 'dir' : 'file', item.status).join(' ');
}

export function walkObject(obj, callback) {
    if (Array.isArray(obj) || isObject(obj)) {
        keys(obj).forEach(key => {
            callback(obj[key], key);
            walkObject(obj[key], callback);
        });
    }
}

export function isAder(user = {}) {
    // 当前用户是不是广告主
    return String(user.roleId) === '7';
}

/**
 * 跨entry的跳转，构造跳转url
 *
 * @param {string} entry entry名
 * @param {string} [hashPath] hash路径
 * @param {Object} [params] hash参数
 */
export function entryRedirect(entry, hashPath, params) {
    let path = `/${entry}/index.html${location.search}${hashPath ? '#' + uri.withQuery(hashPath, params) : ''}`;
    location.href = path;
}

/**
 * 从一个 stringify 的 str 里面取值
 * @param {string} keyStr
 * @param {string} data
 * @param {any} fallback
 * @return {Object} result
 */
export function deepParse(data, keyStr, fallback) {
    let parsedResult = data;
    const keys = keyStr.split('.');
    if (typeof data === 'string') {
        try {
            parsedResult = JSON.parse(data);
        }
        catch (error) {
            throw '传入的data必须为string类型';
        }
    }
    while (keys.length) {
        const key = keys.shift();
        const value = parsedResult[key];
        if (!value) {
            return fallback;
        }
        if (typeof value === 'string') {
            parsedResult = JSON.parse(value);
        }
        else {
            parsedResult = value;
        }
    }
    return parsedResult || fallback;
}

export function safeParse(source, fallback) {
    let result;
    try {
        result = JSON.parse(source);
    }
    catch (e) {
        if (fallback) {
            result = fallback;
        }
    }
    return result;
}

function defaultCallback() {
    alertManager.error(
        '页面长时间未操作，登录状态可能已过期。请备份已填写数据并刷新该页面！',
        '系统提示'
    );
}

/**
 * 粗略监测当前 session 是否已经过期
 *
 * @param {number} expireTime 页面上 session 过期的时间，按毫秒计算
 * @param {Array} events 监听的事件列表
 * @param {Function} callback session 过期时的回调函数
 * @return {Function} unmonitor 取消事件监听的函数
 */
export function monitorSession(expireTime, events = ['click', 'keydown'], callback = defaultCallback) {
    let lastActiveTime = Date.now();

    // 更新上次页面活动时间戳
    function updateActiveTime() {
        lastActiveTime = Date.now();
    }
    // remove registration
    function unmonitor() {
        events.forEach(item => {
            document.removeEventListener(item, updateActiveTime, false);
        });
    }
    // 如果已经过期，则直接提示用户，否则注册下一次的 check 回调
    function checkSession() {
        const inactiveTime = Date.now() - lastActiveTime;
        if (inactiveTime > expireTime) {
            unmonitor();
            callback();
        }
        else {
            setTimeout(checkSession, expireTime - inactiveTime);
        }
    }
    events.forEach(item => {
        document.addEventListener(item, updateActiveTime, false);
    });
    setTimeout(checkSession, expireTime);
    return unmonitor;
}

export function httpsTransform(value) {
    return typeof value === 'string' ? value.replace(/^http:\/\//, 'https://') : value;
}
